import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Carousel from './Carousel.jsx';
import PairingScreen from './PairingScreen.jsx';
import { MODULES, panelsFor } from './modules/index.js';
import { checkForNewBundle, watchServerVersion } from './updates.js';
import {
  DEFAULT_SETTINGS,
  PAGE_SIZE,
  clearDeviceToken,
  fetchPreload,
  getDeviceToken,
  isPreview,
  modulePages,
  recallSettings,
  rememberSettings,
  takeCodeFromURL,
} from './api.js';

// How often a paired display re-reads what it is showing. Not the sync cadence
// - a bookshelf only syncs daily - but the human one: somebody changes the sort
// order, or presses Sync on the admin site, and then looks at the television.
// Bounded staleness of five minutes, at two requests a display.
const REFRESH_MS = 5 * 60 * 1000;
// Spread across the fleet, for the same reason the pairing poll is: displays
// switched on together should not come back to the backend together.
const REFRESH_JITTER_MS = 60 * 1000;
// How long a module's items are trusted on the strength of an unchanged
// revision before they are read again anyway. The revision moves when a sync
// records that it stored items, and a sync that stored them and then failed to
// record it would otherwise leave a display behind until the next one.
const REREAD_MS = 60 * 60 * 1000;

export default function App() {
  // Preview renders inside the admin site, where there is no device token and
  // no pairing to do - the signed-in session stands in for one.
  const preview = isPreview();
  // A screen with no device token has not been paired yet, so it shows a
  // pairing code instead of content. Opening a share link counts as paired:
  // takeCodeFromURL stores the token it carries before this reads it.
  const [paired, setPaired] = useState(() => {
    if (preview) return true;
    return Boolean(takeCodeFromURL() || getDeviceToken());
  });
  const [unauthorized, setUnauthorized] = useState(false);
  // Items keyed by module id, so each module's panels read only their own data.
  const [itemsByModule, setItemsByModule] = useState({});
  // How the panels draw what they are given - which layout the shelf uses, and
  // whatever the photo frame turns out to need.
  //
  // Started from whatever this display drew with last time, when there is
  // one - see recallSettings - and `settingsKnown` says whether there is
  // anything behind the value yet. It decides what the loading screen is:
  // the theme's own once it is known, and nothing at all before that, since
  // a guess is the wrong theme for somebody. New accounts start on one theme
  // and the fallback is the other.
  const [remembered] = useState(recallSettings);
  const [settings, setSettings] = useState(() => ({ ...DEFAULT_SETTINGS, ...remembered }));
  const [settingsKnown, setSettingsKnown] = useState(remembered !== null);
  const [loading, setLoading] = useState(true);
  // What the preload last said about each module - { summary, revision } -
  // and whether every page of a module's items is on the screen yet. A theme
  // that can draw ahead is handed both, and lays itself out for what is coming
  // while it arrives.
  const [outline, setOutline] = useState({});
  const [complete, setComplete] = useState({});
  // Per module: the revision whose every page is on the screen, and when it
  // was read. What lets a refresh with nothing new fetch nothing but the
  // preload - which on a library of twenty pages is the difference between
  // one request every five minutes and twenty-one.
  const loaded = useRef({});
  // Per module: whether anything has been put on the screen for it yet. The
  // first load draws page by page; a refresh draws once, at the end, because
  // a shelf that shrank to its first page and grew back every five minutes
  // would be a television flickering in somebody's living room.
  const shown = useRef({});
  const [requestedIndex, setActiveIndex] = useState(0);
  // What the next panel should open on - a book id for the highlight view, and
  // whatever the equivalent turns out to be for photos.
  const [focus, setFocus] = useState(null);

  // Which panels exist at all depends on the theme the account has chosen -
  // the classic bookshelf is two, the eight-bit level is one - so this is
  // derived rather than constant. See modules/index.js.
  const panels = useMemo(() => panelsFor(settings), [settings]);

  // Switching theme replaces the panel list under the carousel, and the index
  // it was parked on may be past the end of the new one. Corrected during
  // render rather than from an effect, so a shorter list never reaches the
  // DOM: an out-of-range index would draw nothing at all for a frame.
  const activeIndex = Math.max(0, Math.min(requestedIndex, panels.length - 1));

  const goToPanel = useCallback((module, theme, key) => {
    const index = panels.findIndex((panel) => panel.id === `${module.id}:${theme.id}:${key}`);
    if (index >= 0) setActiveIndex(index);
  }, [panels]);

  useEffect(() => {
    if (!paired) return undefined;
    let cancelled = false;
    let timer;

    // Each load is numbered, and only the latest may change the screen. A tab
    // coming back into view starts a load whether or not one is running, and
    // two walks through the same pages writing to the screen in turn would
    // leave it showing whichever lost the race.
    let latest = 0;

    // A 401 means this display is not allowed to read any more. Answered here
    // for the preload and the pages alike; true when it was.
    function signedOut(error) {
      if (error?.status !== 401) return false;
      if (cancelled) return true;
      if (preview) {
        // Previewing without a live admin session; say so rather than
        // dropping into a pairing code the admin cannot act on.
        setUnauthorized(true);
        setLoading(false);
        return true;
      }
      // A revoked or expired device token means this screen is no longer
      // paired; drop it and show a fresh pairing code.
      clearDeviceToken();
      setPaired(false);
      return true;
    }

    function show(moduleId, items, done) {
      shown.current[moduleId] = true;
      setItemsByModule((current) => ({ ...current, [moduleId]: items }));
      setComplete((current) => (current[moduleId] === done ? current : { ...current, [moduleId]: done }));
      setLoading(false);
    }

    async function loadModule(module, about, pageSize, current) {
      const had = loaded.current[module.id];
      if (about?.revision && had?.revision === about.revision && Date.now() - had.at < REREAD_MS) return;

      const progressive = !shown.current[module.id];
      let items = [];
      try {
        for await (const page of modulePages(module.id, pageSize)) {
          if (!current()) return;
          items = items.concat(module.unwrap(page));
          if (progressive) show(module.id, items, false);
        }
      } catch (error) {
        if (signedOut(error)) return;
        console.error(`Failed to load ${module.id}`, error);
        // Whatever is on the screen stays there - a living room should not go
        // blank because one poll missed the backend - and nothing is marked
        // as read, so the next tick walks the pages again. A first load that
        // failed before its first page still has to leave the loading screen.
        if (current() && !shown.current[module.id]) show(module.id, [], false);
        return;
      }
      if (!current()) return;
      show(module.id, items, true);
      loaded.current[module.id] = { revision: about?.revision ?? null, at: Date.now() };
    }

    async function load(generation) {
      const current = () => !cancelled && generation === latest;

      // The preload is a head start, not a gate: a display that cannot read it
      // still pages its books in, in the settings it already has. A failure is
      // an answer too, as far as the loading screen goes - the books will be
      // drawn with what is there, so that is what it should look like.
      let preload = null;
      try {
        preload = await fetchPreload();
      } catch (error) {
        if (signedOut(error)) return;
        console.error('Failed to load the preload', error);
      }
      if (!current()) return;
      setSettingsKnown(true);
      if (preload) {
        setSettings({ ...DEFAULT_SETTINGS, ...preload.settings });
        rememberSettings(preload.settings);
        setOutline(preload.modules ?? {});
      }

      // Modules load independently: a failing photo connection must not blank
      // the bookshelf.
      const pageSize = preload?.pageSize || PAGE_SIZE;
      await Promise.all(MODULES.map((module) => loadModule(module, preload?.modules?.[module.id], pageSize, current)));
    }

    // A display runs for months without anybody touching it, so the content
    // has to come to it. Nothing did this before: the screen fetched once at
    // mount and then showed that forever, however many times the shelf synced
    // underneath it.
    async function tick() {
      // A backgrounded tab is the admin site's preview iframe, not a
      // television. Nobody is looking, so nobody needs the request.
      if (!document.hidden) {
        latest += 1;
        await load(latest);
        // Rides the same tick rather than a clock of its own: new content and
        // new code are the same question asked of a display that has been on
        // since March.
        await checkForNewBundle();
      }
      if (!cancelled) timer = setTimeout(tick, REFRESH_MS + Math.random() * REFRESH_JITTER_MS);
    }

    const onVisible = () => {
      if (document.hidden || cancelled) return;
      clearTimeout(timer);
      tick();
    };

    // Only once paired. An unpaired screen is showing a code somebody may be
    // halfway through typing into the admin site, and reloading it would
    // throw that code away and issue a different one.
    watchServerVersion();

    tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [paired, preview]);

  const hasContent = Object.values(itemsByModule).some((items) => items.length > 0);
  // The loading screen belongs to whichever theme the display opens on.
  const opening = panels[0];
  const Loading = settingsKnown ? opening?.theme.Loading : null;
  // And that theme may not need it for long. One that can draw from the
  // preload alone is shown as soon as the preload says there is something
  // coming, and fills in as the pages arrive - but only until they all have:
  // a summary that promised books the shelf turned out not to have is not a
  // reason to keep drawing an empty room.
  const drawingAhead = Boolean(
    opening?.theme.drawsAhead
      && !complete[opening.module.id]
      && (outline[opening.module.id]?.summary?.items ?? 0) > 0
  );

  if (!paired) {
    return <PairingScreen onPaired={() => { setPaired(true); setLoading(true); }} />;
  }

  return (
    <main className="app-shell">
      {loading && !drawingAhead ? (
        Loading ? <Loading settings={settings} /> : null
      ) : unauthorized ? (
        <p className="status-message">Sign in to the admin site to preview a display.</p>
      ) : !hasContent && !drawingAhead ? (
        <p className="status-message">
          Nothing to show yet. Connect a source on the admin page and sync it.
        </p>
      ) : (
        <Carousel activeIndex={activeIndex} onIndexChange={setActiveIndex}>
          {panels.map((panel, index) => (
            <panel.Component
              key={panel.id}
              data={itemsByModule[panel.module.id] ?? []}
              settings={settings}
              outline={outline[panel.module.id] ?? null}
              complete={Boolean(complete[panel.module.id])}
              active={index === activeIndex}
              focus={focus}
              setFocus={setFocus}
              // Bound to the panel's own theme, so a theme names its panels by
              // key and never has to know what else is loaded beside it.
              goToPanel={(key) => goToPanel(panel.module, panel.theme, key)}
            />
          ))}
        </Carousel>
      )}
    </main>
  );
}
